import { supabase } from "../supabase";

/**
 * Fetch all people in a tree, including their family_links and unions.
 * Returns a flat list that the existing buildTreeLayout() can consume
 * after you map DB rows → the shape family.js currently uses.
 */
export async function fetchPeople(treeId) {
  const peopleRes = await supabase
    .from("people")
    .select("*")
    .eq("tree_id", treeId)
    .order("last_name");

  if (peopleRes.error) return { people: [], error: peopleRes.error };

  const dbPeople = peopleRes.data ?? [];
  if (dbPeople.length === 0) return { people: [], error: null };

  // Fetch only the links/unions that involve people in this tree
  const ids = dbPeople.map((p) => p.id);
  const idList = ids.join(",");

  const [linksRes, unionsRes, siblingRes] = await Promise.all([
    supabase.from("family_links").select("*").in("child_id", ids),
    supabase.from("unions").select("*").or(`person_a_id.in.(${idList}),person_b_id.in.(${idList})`),
    supabase.from("sibling_links").select("*").or(`person_a_id.in.(${idList}),person_b_id.in.(${idList})`),
  ]);

  const links = linksRes.data ?? [];
  const unions = unionsRes.data ?? [];
  const siblingLinks = siblingRes.data ?? [];

  // Map DB rows → the shape buildTreeLayout() expects:
  // { id, firstName, middleName, lastName, nickname, gender, birthYear, birthDate, deathYear,
  //   city, phone, email, address, notes, avatarUrl,
  //   parentIds: [], spouseId: null | string, unions: [] }
  const mapped = dbPeople.map((p) => {
    const myLinks = links.filter((l) => l.child_id === p.id);
    const parentIds = myLinks.map((l) => l.parent_id);
    const parentLinkTypes = {};
    for (const l of myLinks) {
      parentLinkTypes[l.parent_id] = l.relation_type === "adoptive" ? "adoptive" : "biological";
    }

    const mySiblings = siblingLinks
      .filter((s) => s.person_a_id === p.id || s.person_b_id === p.id)
      .map((s) => s.person_a_id === p.id ? s.person_b_id : s.person_a_id);

    const myUnions = unions.filter(
      (u) => u.person_a_id === p.id || u.person_b_id === p.id
    );
    const mappedUnions = myUnions.map((u) => ({
      spouseId: u.person_a_id === p.id ? u.person_b_id : u.person_a_id,
      unionStatus: (u.union_status ?? "married") === "former" ? "former" : "married",
    }));
    const spouseId = myUnions.length === 1 ? mappedUnions[0].spouseId : null;
    const unionsOut = mappedUnions.length > 1 ? mappedUnions : myUnions.length === 1 ? mappedUnions : [];

    return {
      id:               p.id,
      firstName:        p.first_name,
      middleName:       p.middle_name ?? null,
      lastName:         p.last_name,
      nickname:         p.nickname ?? null,
      gender:           p.gender,
      birthYear:        p.birth_year,
      birthDate:        p.birth_date ?? null,
      deathYear:        p.death_year,
      city:             p.city,
      country:          p.country,
      birthCity:        p.birth_city,
      birthCountry:     p.birth_country,
      phone:            p.phone,
      email:            p.email,
      address:          p.address,
      notes:            p.notes,
      avatarUrl:        p.avatar_url,
      claimedByUserId:  p.claimed_by_user_id,
      parentIds,
      parentLinkTypes: Object.keys(parentLinkTypes).length ? parentLinkTypes : undefined,
      siblingIds: mySiblings.length > 0 ? mySiblings : undefined,
      spouseId,
      unions: unionsOut,
    };
  });

  return { people: mapped, error: null };
}

/** Insert a new person and their family/union links.
 *  connectionType: "child_of" | "sibling_of" | "parent_of" | "spouse_of"
 *  connectedPersonId: the existing person this new one connects to */
export async function insertPerson(treeId, personData) {
  const { connectedPersonId, connectionType, parentRelation, spouseUnionStatus, coParentId, coParentRelation, ...fields } = personData;

  const { data: person, error: personErr } = await supabase
    .from("people")
    .insert({
      tree_id:       treeId,
      first_name:    fields.firstName,
      middle_name:   fields.middleName?.trim() || null,
      last_name:     fields.lastName,
      nickname:      fields.nickname?.trim() || null,
      gender:        fields.gender ?? "male",
      birth_year:    fields.birthYear ?? null,
      birth_date:    fields.birthDate ?? null,
      death_year:    fields.deathYear ?? null,
      city:          fields.city ?? null,
      country:       fields.country ?? null,
      birth_city:    fields.birthCity ?? null,
      birth_country: fields.birthCountry ?? null,
      phone:         fields.phone ?? null,
      email:         fields.email ?? null,
      address:       fields.address ?? null,
      notes:         fields.notes ?? null,
    })
    .select()
    .single();

  if (personErr) return { person: null, error: personErr };

  if (connectedPersonId && connectionType) {
    const parentRel =
      parentRelation === "adoptive" ? "adoptive" : "biological";
    if (connectionType === "child_of") {
      const rows = [
        {
          child_id: person.id,
          parent_id: connectedPersonId,
          relation_type: parentRel,
        },
      ];
      if (
        coParentId &&
        coParentId !== connectedPersonId
      ) {
        const coRel =
          coParentRelation === "adoptive" ? "adoptive" : "biological";
        rows.push({
          child_id: person.id,
          parent_id: coParentId,
          relation_type: coRel,
        });
      }
      await supabase.from("family_links").insert(rows);
    } else if (connectionType === "sibling_of") {
      // Sibling: copy the parents of the connected person to the new person (if any)
      const { data: siblingParentLinks } = await supabase
        .from("family_links")
        .select("parent_id, relation_type")
        .eq("child_id", connectedPersonId);
      if (siblingParentLinks && siblingParentLinks.length > 0) {
        const rows = siblingParentLinks.map((link) => ({
          child_id: person.id,
          parent_id: link.parent_id,
          relation_type: parentRel,
        }));
        await supabase.from("family_links").insert(rows);
      }
      // Always store an explicit sibling link (works even without parents)
      const [a, b] = [person.id, connectedPersonId].sort();
      await supabase.from("sibling_links").insert({ person_a_id: a, person_b_id: b });
    } else if (connectionType === "parent_of") {
      await supabase.from("family_links").insert({
        child_id: connectedPersonId,
        parent_id: person.id,
        relation_type: parentRel,
      });
    } else if (connectionType === "spouse_of") {
      const [a, b] = [person.id, connectedPersonId].sort();
      const ustat = spouseUnionStatus === "former" ? "former" : "married";
      await supabase.from("unions").insert({
        person_a_id: a,
        person_b_id: b,
        union_status: ustat,
      });
    }
  }

  return { person, error: null };
}

/** Update a person's fields. Pass only the fields that changed. */
export async function updatePerson(personId, updates) {
  const dbUpdates = {};
  if ("firstName"        in updates) dbUpdates.first_name         = updates.firstName;
  if ("middleName"       in updates) dbUpdates.middle_name        = updates.middleName?.trim() || null;
  if ("lastName"         in updates) dbUpdates.last_name          = updates.lastName;
  if ("nickname"         in updates) dbUpdates.nickname          = updates.nickname?.trim() || null;
  if ("gender"           in updates) dbUpdates.gender             = updates.gender;
  if ("birthYear"        in updates) dbUpdates.birth_year         = updates.birthYear;
  if ("birthDate"        in updates) dbUpdates.birth_date       = updates.birthDate;
  if ("deathYear"        in updates) dbUpdates.death_year         = updates.deathYear;
  if ("city"             in updates) dbUpdates.city               = updates.city;
  if ("country"          in updates) dbUpdates.country            = updates.country;
  if ("birthCity"        in updates) dbUpdates.birth_city         = updates.birthCity;
  if ("birthCountry"     in updates) dbUpdates.birth_country      = updates.birthCountry;
  if ("phone"            in updates) dbUpdates.phone              = updates.phone;
  if ("email"            in updates) dbUpdates.email              = updates.email;
  if ("address"          in updates) dbUpdates.address            = updates.address;
  if ("notes"            in updates) dbUpdates.notes              = updates.notes;
  if ("avatarUrl"        in updates) dbUpdates.avatar_url         = updates.avatarUrl;
  if ("claimedByUserId"  in updates) dbUpdates.claimed_by_user_id = updates.claimedByUserId;

  const { data, error } = await supabase
    .from("people")
    .update(dbUpdates)
    .eq("id", personId)
    .select()
    .single();

  return { person: data, error };
}

/** Add a union between two people. unionStatus: 'married' | 'former' */
export async function addUnion(personAId, personBId, unionStatus = "married") {
  const [a, b] = [personAId, personBId].sort();
  const status = unionStatus === "former" ? "former" : "married";
  const { error } = await supabase.from("unions").insert({
    person_a_id: a,
    person_b_id: b,
    union_status: status,
  });
  return { error };
}

/** Update union status (e.g. married → former) without removing the link. */
export async function updateUnionStatus(personAId, personBId, unionStatus) {
  const [a, b] = [personAId, personBId].sort();
  const status = unionStatus === "former" ? "former" : "married";
  const { error } = await supabase
    .from("unions")
    .update({ union_status: status })
    .eq("person_a_id", a)
    .eq("person_b_id", b);
  return { error };
}

/** Remove a union (marriage) between two people. */
export async function removeUnion(personAId, personBId) {
  const [a, b] = [personAId, personBId].sort();
  const { error } = await supabase.from("unions").delete()
    .eq("person_a_id", a).eq("person_b_id", b);
  return { error };
}

/**
 * Replace parent links for a child. Each entry: { parentId, relationType?: 'biological'|'adoptive' }.
 */
export async function replaceParentLinks(personId, parentEntries = []) {
  const { error: deleteError } = await supabase
    .from("family_links")
    .delete()
    .eq("child_id", personId);
  if (deleteError) return { error: deleteError };

  if (!parentEntries.length) return { error: null };

  const rows = parentEntries.map(({ parentId, relationType }) => ({
    child_id: personId,
    parent_id: parentId,
    relation_type: relationType === "adoptive" ? "adoptive" : "biological",
  }));
  const { error } = await supabase.from("family_links").insert(rows);
  return { error };
}

/** @deprecated Use replaceParentLinks with explicit relation types. */
export async function replaceParents(personId, parentIds = []) {
  return replaceParentLinks(
    personId,
    parentIds.map((parentId) => ({ parentId, relationType: "biological" }))
  );
}

/** Delete a person (family_links and unions cascade automatically). */
export async function deletePerson(personId) {
  const { error } = await supabase.from("people").delete().eq("id", personId);
  return { error };
}

/** Delete a person and their entire descendant subtree via a single RPC call. */
export async function deletePersonWithSubtree(personId) {
  const { error } = await supabase.rpc("delete_person_subtree", {
    root_person_id: personId,
  });
  return { error };
}
